import ClickOutside from 'app/widgets/ClickOutside';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { AdContext } from '../ReactContexts/adContext';
import {
  DealerListContext,
  DealerTabs,
} from '../ReactContexts/dealerListContext';
import { EquipmentListContext } from '../ReactContexts/equipmentListContext';
import { LayoutContext } from '../ReactContexts/layoutContext';
import { VerifyEmailMenu } from '../VerifyEmailMenu';
import { SmallAdBanner } from './components/SmallAdBanner';
import useFlag from 'app/hooks/useFlag';
import { FeatureName } from 'types/entities/MobileSettings';
import { LanguageSwitcher } from 'app/components/LanguageSwitcher';
import { useMemo } from 'react';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { useAuth } from 'context';
import { mobileSettingsApi } from 'services/api/mobile-settings/queries';
import { Logo } from 'app/components/logos';
import './KubotaMenu.less';

export function KubotaMenu() {
  const isAccessSharingPageEnabled = useFlag(FeatureName.ACCESS_SHARING);

  const { data: user } = useGetUserInfoQuery();
  const { isAuthenticated } = useAuth();

  const { removeSpecificClass, classNames, setClassNames } =
    React.useContext(LayoutContext);
  const { equipmentListData } = React.useContext(EquipmentListContext);
  const { adData, setAdData } = React.useContext(AdContext);
  const { data: { ad } = {} } = mobileSettingsApi.useGetMobileSettingsQuery();
  const { setDealerListData } = React.useContext(DealerListContext);

  const animateMobileNav = () => {
    setClassNames('mobile-nav-animating');
    window.setTimeout(() => {
      removeSpecificClass('mobile-nav-animating');
    }, 600);
  };

  const handleClick = () => {
    animateMobileNav();
    removeSpecificClass('mobile-nav-shown');
  };

  const handleClickOutside = () => {
    if (
      classNames.filter(className => className.class == 'mobile-nav-shown')
        .length > 0
    )
      handleClick();
  };

  const shouldShowSmallAdBanner = () => {
    return (
      !!adData?.smallBannerAd?.buttonLabel &&
      !!adData?.smallBannerAd?.buttonUrl &&
      !!adData?.smallBannerAd?.description &&
      !!adData?.smallBannerAd?.imageUrl &&
      !!adData?.smallBannerAd?.title
    );
  };

  React.useEffect(() => {
    if (!shouldShowSmallAdBanner() && ad) {
      const { locale, ...adData } = ad;
      // TODO eliminate this useless AdContext at a later time
      setAdData(adData as any);
    }
  }, []);

  function handleLogoClick() {
    if (isAuthenticated) {
      navigate('/equipment', {
        state: { scrollToEquipmentId: equipmentListData.selectedEquipment?.id },
      });
    } else {
      navigate('/login');
    }

    handleClick();
  }

  const handleEquipmentClick = () => {
    removeSpecificClass('equipment-details-visible');
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isMenuOpen = useMemo(
    () => classNames.some(className => className.class === 'mobile-nav-shown'),
    [classNames],
  );

  let selectedIndex = -1;

  if (location.pathname.includes('/equipment')) {
    selectedIndex = 0;
  }
  if (
    location.pathname.includes('/resources') ||
    location.pathname.includes('/models') ||
    matchPath('/faultCodes/:model/', location.pathname) ||
    location.pathname.includes('/manual') ||
    location.pathname.includes('/videoResources') ||
    location.pathname.includes('/guides')
  ) {
    selectedIndex = 2;
  }
  if (location.pathname.includes('/dealers')) {
    selectedIndex = 3;
  }
  if (location.pathname.includes('/access')) {
    selectedIndex = 4;
  }
  if (location.pathname.includes('/about')) {
    selectedIndex = 5;
  }

  const EquipmentSvg = (
    <svg
      id="ic_menu_equipment"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M13.5 16.875H16.875H13.5ZM16.875 16.875H20.25H16.875ZM16.875 16.875V13.5V16.875ZM16.875 16.875V20.25V16.875ZM6 10.5H8.25C8.84674 10.5 9.41903 10.2629 9.84099 9.84099C10.2629 9.41903 10.5 8.84674 10.5 8.25V6C10.5 5.40326 10.2629 4.83097 9.84099 4.40901C9.41903 3.98705 8.84674 3.75 8.25 3.75H6C5.40326 3.75 4.83097 3.98705 4.40901 4.40901C3.98705 4.83097 3.75 5.40326 3.75 6V8.25C3.75 8.84674 3.98705 9.41903 4.40901 9.84099C4.83097 10.2629 5.40326 10.5 6 10.5ZM6 20.25H8.25C8.84674 20.25 9.41903 20.0129 9.84099 19.591C10.2629 19.169 10.5 18.5967 10.5 18V15.75C10.5 15.1533 10.2629 14.581 9.84099 14.159C9.41903 13.7371 8.84674 13.5 8.25 13.5H6C5.40326 13.5 4.83097 13.7371 4.40901 14.159C3.98705 14.581 3.75 15.1533 3.75 15.75V18C3.75 18.5967 3.98705 19.169 4.40901 19.591C4.83097 20.0129 5.40326 20.25 6 20.25ZM15.75 10.5H18C18.5967 10.5 19.169 10.2629 19.591 9.84099C20.0129 9.41903 20.25 8.84674 20.25 8.25V6C20.25 5.40326 20.0129 4.83097 19.591 4.40901C19.169 3.98705 18.5967 3.75 18 3.75H15.75C15.1533 3.75 14.581 3.98705 14.159 4.40901C13.7371 4.83097 13.5 5.40326 13.5 6V8.25C13.5 8.84674 13.7371 9.41903 14.159 9.84099C14.581 10.2629 15.1533 10.5 15.75 10.5Z"
        fill="none"
      />
      <path
        d="M13.5 16.875H16.875M16.875 16.875H20.25M16.875 16.875V13.5M16.875 16.875V20.25M6 10.5H8.25C8.84674 10.5 9.41903 10.2629 9.84099 9.84099C10.2629 9.41903 10.5 8.84674 10.5 8.25V6C10.5 5.40326 10.2629 4.83097 9.84099 4.40901C9.41903 3.98705 8.84674 3.75 8.25 3.75H6C5.40326 3.75 4.83097 3.98705 4.40901 4.40901C3.98705 4.83097 3.75 5.40326 3.75 6V8.25C3.75 8.84674 3.98705 9.41903 4.40901 9.84099C4.83097 10.2629 5.40326 10.5 6 10.5ZM6 20.25H8.25C8.84674 20.25 9.41903 20.0129 9.84099 19.591C10.2629 19.169 10.5 18.5967 10.5 18V15.75C10.5 15.1533 10.2629 14.581 9.84099 14.159C9.41903 13.7371 8.84674 13.5 8.25 13.5H6C5.40326 13.5 4.83097 13.7371 4.40901 14.159C3.98705 14.581 3.75 15.1533 3.75 15.75V18C3.75 18.5967 3.98705 19.169 4.40901 19.591C4.83097 20.0129 5.40326 20.25 6 20.25ZM15.75 10.5H18C18.5967 10.5 19.169 10.2629 19.591 9.84099C20.0129 9.41903 20.25 8.84674 20.25 8.25V6C20.25 5.40326 20.0129 4.83097 19.591 4.40901C19.169 3.98705 18.5967 3.75 18 3.75H15.75C15.1533 3.75 14.581 3.98705 14.159 4.40901C13.7371 4.83097 13.5 5.40326 13.5 6V8.25C13.5 8.84674 13.7371 9.41903 14.159 9.84099C14.581 10.2629 15.1533 10.5 15.75 10.5Z"
        stroke="#909090"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const ResourcesSvg =
    selectedIndex === 2 ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 22 19"
        fill="none"
      >
        <path
          d="M14.6602 18.228H18.8502C19.1182 18.2267 19.3831 18.1703 19.6283 18.0621C19.8735 17.954 20.0939 17.7965 20.2756 17.5995C20.4573 17.4025 20.5965 17.1702 20.6845 16.917C20.7726 16.6639 20.8075 16.3953 20.7872 16.128L19.9202 4.08203"
          stroke="#DC4405"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8.07422 4.11133C8.00591 4.37636 8.04182 4.65782 8.17578 4.89648C8.30974 5.13515 8.53115 5.31284 8.79297 5.39258C8.91787 5.41213 9.04366 5.42521 9.16992 5.43164C9.40693 5.41974 9.63366 5.33108 9.81641 5.17969C9.99916 5.0283 10.1278 4.8205 10.1836 4.58984L10.875 1.69141H14.4316L13.8477 4.14062C13.7843 4.39791 13.8259 4.66967 13.9629 4.89648C14.061 5.09967 14.2221 5.26412 14.4238 5.36523C14.6255 5.46635 14.8566 5.49804 15.0781 5.45508C15.3115 5.436 15.5314 5.34228 15.707 5.1875C15.8827 5.03272 16.0044 4.82479 16.0527 4.5957L16.752 1.67969L18.0371 1.69141C18.5304 1.69078 19.0122 1.83843 19.4219 2.11328C19.8316 2.38813 20.1514 2.77765 20.3379 3.23438C20.5488 3.7345 20.5926 4.28937 20.4629 4.81641L20.457 4.82227L16.8555 18.8281L16.8496 18.8242H0L4.74805 3.46094C4.90516 2.95105 5.21885 2.50461 5.64648 2.18555C6.07411 1.86649 6.59344 1.6929 7.12695 1.6875L8.65039 1.67383L8.07422 4.11133ZM6.50586 12.3242C6.17467 12.3244 5.90625 12.5926 5.90625 12.9238C5.9063 13.255 6.1747 13.5232 6.50586 13.5234H13.5195C13.8507 13.5233 14.1191 13.2551 14.1191 12.9238C14.1191 12.5926 13.8508 12.3244 13.5195 12.3242H6.50586ZM7.38184 8.78906C7.05064 8.78927 6.78223 9.05743 6.78223 9.38867C6.78228 9.71987 7.05068 9.98807 7.38184 9.98828H14.3955C14.7267 9.98813 14.9951 9.7199 14.9951 9.38867C14.9951 9.05739 14.7267 8.78921 14.3955 8.78906H7.38184Z"
          fill="#DC4405"
        />
        <path
          d="M9.11914 4.41861L10.0311 0.599609"
          stroke="#DC4405"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M14.9043 4.43033L15.8163 0.611328"
          stroke="#DC4405"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          id="Vector"
          d="M21.066 7.16497L17.601 20.638H2L6.497 6.09497C6.61853 5.69097 6.86623 5.33651 7.20382 5.0835C7.5414 4.83049 7.95114 4.69223 8.373 4.68897H19.155C19.5446 4.68855 19.9255 4.80444 20.2488 5.02178C20.5722 5.23913 20.8233 5.54804 20.97 5.90897C21.1387 6.30581 21.1725 6.74709 21.066 7.16497Z"
          stroke="#909090"
          stroke-width="1.2"
        />
        <path
          id="Vector-2"
          d="M15.8672 20.6382H20.0572C20.3252 20.6369 20.5901 20.5804 20.8353 20.4723C21.0806 20.3642 21.3009 20.2067 21.4826 20.0097C21.6644 19.8127 21.8036 19.5803 21.8916 19.3272C21.9796 19.074 22.0145 18.8054 21.9942 18.5382L21.1272 6.49219"
          stroke="#909090"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          id="Vector-3"
          d="M10.3281 6.82779L11.2401 3.00879"
          stroke="#909090"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          id="Vector-4"
          d="M16.2012 6.837L17.0252 3"
          stroke="#909090"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          id="Vector-5"
          d="M8.5918 11.7969H15.6048"
          stroke="#909090"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          id="Vector-6"
          d="M7.71484 15.333H14.7278"
          stroke="#909090"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );

  const DealersSvg = (
    <svg
      id="ic_menu_dealers"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <rect id="Box" width="24" height="24" fill="none" />
      <g id="Icon">
        <path
          id="Vector"
          d="M15.368.162,5.751,2.573A4.786,4.786,0,0,0,2.574,5.75L.162,15.367c-.726,2.906,1.056,4.7,3.971,3.971l9.617-2.4a4.825,4.825,0,0,0,3.177-3.177l2.412-9.627C20.065,1.227,18.274-.565,15.368.162Z"
          transform="translate(2.25 2.322)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-2"
          d="M6,3A3,3,0,1,1,3,0,3,3,0,0,1,6,3Z"
          transform="translate(9 9.072)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
      </g>
    </svg>
  );

  const AccessSharingSvg =
    selectedIndex != 4 ? (
      <svg
        id="ic_menu_access_sharing"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <rect id="BG" width="24" height="24" fill="none" />
        <g id="icon" transform="translate(2 2.994)">
          <g id="Person" transform="translate(5.967 2.636)">
            <g
              id="Rectangle_315"
              data-name="Rectangle 315"
              transform="translate(0 8.358)"
              fill="none"
              stroke="#909090"
              strokeWidth="1.2"
            >
              <path
                d="M3,0h8.033a3,3,0,0,1,3,3V7.016a0,0,0,0,1,0,0H0a0,0,0,0,1,0,0V3A3,3,0,0,1,3,0Z"
                stroke="none"
              />
              <path
                d="M3,.6h8.033a2.4,2.4,0,0,1,2.4,2.4V5.816a.6.6,0,0,1-.6.6H1.2a.6.6,0,0,1-.6-.6V3A2.4,2.4,0,0,1,3,.6Z"
                fill="none"
              />
            </g>
            <path
              id="Path_1103"
              data-name="Path 1103"
              d="M22522-5571.061h0a.217.217,0,0,0-.068-.006h-.145a3.8,3.8,0,0,1-2.6-1.106,3.613,3.613,0,0,1-1.037-2.546,3.666,3.666,0,0,1,3.664-3.659,3.663,3.663,0,0,1,3.658,3.659,3.667,3.667,0,0,1-3.477,3.658Zm-.168-1.32h0a.388.388,0,0,1,.043,0h.008l.035,0,.02.006.021-.006a2.367,2.367,0,0,0,2.223-2.351,2.362,2.362,0,0,0-2.363-2.357,2.358,2.358,0,0,0-2.355,2.357,2.347,2.347,0,0,0,2.262,2.351l.1.006.006-.012Z"
              transform="translate(-22514.797 5578.378)"
              fill="#909090"
            />
          </g>
          <g id="Person-2" data-name="Person">
            <path
              id="Path_1105"
              data-name="Path 1105"
              d="M23264.779-5381.905h-.184a2.69,2.69,0,0,1-2.6-2.675,2.692,2.692,0,0,1,2.682-2.691,2.687,2.687,0,0,1,2.688,2.681,2.679,2.679,0,0,1-2.582,2.686Zm-.072-.992c.037,0,.057.006.072.006h.025l.016,0h.021a1.686,1.686,0,0,0,1.555-1.7,1.694,1.694,0,0,0-1.7-1.688,1.689,1.689,0,0,0-1.687,1.688,1.7,1.7,0,0,0,1.6,1.7h.049v-.011Z"
              transform="translate(-23259.313 5387.271)"
              fill="#909090"
            />
            <path
              id="Path_1104"
              data-name="Path 1104"
              d="M23154.357-5308.648h4.422s.783.042.77-.6-.77-.552-.77-.552h-3.273v-1.082a1.715,1.715,0,0,1,1.715-1.714h3.736a.571.571,0,0,0,.459-.593.512.512,0,0,0-.459-.555h-3.736a2.866,2.866,0,0,0-2.863,2.862v2.229Z"
              transform="translate(-23154.357 5320.157)"
              fill="#909090"
            />
          </g>
        </g>
      </svg>
    ) : (
      <svg
        id="ic_menu_access_sharing_selected"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <rect id="BG" width="24" height="24" fill="none" />
        <g id="icon" transform="translate(-616.646 -257.207)">
          <g id="Person" transform="translate(624.614 262.837)">
            <g
              id="Rectangle_315"
              data-name="Rectangle 315"
              transform="translate(0 8.358)"
              fill="#dc4405"
              stroke="#dc4405"
              strokeWidth="1.2"
            >
              <path
                d="M3,0h8.033a3,3,0,0,1,3,3V7.016a0,0,0,0,1,0,0H0a0,0,0,0,1,0,0V3A3,3,0,0,1,3,0Z"
                stroke="none"
              />
              <path
                d="M3,.6h8.033a2.4,2.4,0,0,1,2.4,2.4V5.816a.6.6,0,0,1-.6.6H1.2a.6.6,0,0,1-.6-.6V3A2.4,2.4,0,0,1,3,.6Z"
                fill="none"
              />
            </g>
            <path
              id="Path_1103"
              data-name="Path 1103"
              d="M22522-5571.061h0a.217.217,0,0,0-.068-.006h-.145a3.8,3.8,0,0,1-2.6-1.106,3.613,3.613,0,0,1-1.037-2.546,3.666,3.666,0,0,1,3.664-3.659,3.663,3.663,0,0,1,3.658,3.659,3.667,3.667,0,0,1-3.477,3.658Z"
              transform="translate(-22514.797 5578.378)"
              fill="#dc4405"
            />
          </g>
          <g
            id="Person-2"
            data-name="Person"
            transform="translate(618.646 260.201)"
          >
            <path
              id="Path_1105"
              data-name="Path 1105"
              d="M23264.779-5381.905h-.184a2.69,2.69,0,0,1-2.6-2.675,2.692,2.692,0,0,1,2.682-2.691,2.687,2.687,0,0,1,2.688,2.681,2.679,2.679,0,0,1-2.582,2.686Zm-.072-.992c.037,0,.057.006.072.006h.025l.016,0h.021a1.686,1.686,0,0,0,1.555-1.7,1.694,1.694,0,0,0-1.7-1.688,1.689,1.689,0,0,0-1.687,1.688,1.7,1.7,0,0,0,1.6,1.7h.049v-.011Z"
              transform="translate(-23259.313 5387.271)"
              fill="#dc4405"
            />
            <path
              id="Path_1104"
              data-name="Path 1104"
              d="M23154.357-5308.648h4.422s.783.042.77-.6-.77-.552-.77-.552h-3.273v-1.082a1.715,1.715,0,0,1,1.715-1.714h3.736a.571.571,0,0,0,.459-.593.512.512,0,0,0-.459-.555h-3.736a2.866,2.866,0,0,0-2.863,2.862v2.229Z"
              transform="translate(-23154.357 5320.157)"
              fill="#dc4405"
            />
          </g>
        </g>
      </svg>
    );

  const AboutSvg = (
    <svg
      id="ic_menu_about"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <rect id="Box" width="24" height="24" fill="none" />
      <g id="icon">
        <path
          id="Vector"
          d="M9,18A9,9,0,1,0,0,9,9.026,9.026,0,0,0,9,18Z"
          transform="translate(21 21) rotate(180)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-2"
          d="M0,0V4.5"
          transform="translate(12 15.6) rotate(180)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.2"
        />
        <path
          id="Vector-3"
          d="M0,0H.008"
          transform="translate(12.006 8.4) rotate(180)"
          fill="none"
          stroke="#909090"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </g>
    </svg>
  );

  const NavClose = (
    <svg
      id="ic_nav_close"
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
    >
      <rect id="Box" width="32" height="32" fill="none" />
      <g id="Icon" transform="translate(-2076.936 -711.936)">
        <line
          id="Line_1"
          data-name="Line 1"
          x2="12"
          y2="12"
          transform="translate(2086.935 721.935)"
          fill="none"
          stroke="#1a1a1a"
          strokeLinecap="round"
          strokeWidth="2"
        />
        <line
          id="Line_2"
          data-name="Line 2"
          x1="12"
          y2="12"
          transform="translate(2086.935 721.935)"
          fill="none"
          stroke="#1a1a1a"
          strokeLinecap="round"
          strokeWidth="2"
        />
      </g>
    </svg>
  );
  return (
    <>
      <ClickOutside
        onClickOutside={handleClickOutside}
        className="layout-clickoutside-submenu"
      >
        <div className="menu-header kubota-menu">
          <span onClick={handleLogoClick}>
            <Logo />
          </span>
          <span className="mobile-menu-close" onClick={handleClick}>
            {NavClose}
          </span>
        </div>

        {!isAuthenticated && (
          <LanguageSwitcher
            className="kubota-menu__language-switcher"
            hidden={!isMenuOpen}
          />
        )}

        <div className="nav-side">
          <div className="menu-left">
            <ul className="k-reset k-header k-menu k-menu-vertical">
              {isAuthenticated && (
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 0 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link
                    to="/equipment"
                    state={{
                      scrollToEquipmentId:
                        equipmentListData.selectedEquipment?.id,
                    }}
                    onClick={() => {
                      handleClick();
                      location.pathname.includes('/equipment') &&
                        handleEquipmentClick();
                    }}
                  >
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon menu-icon-equip">
                        {EquipmentSvg}
                      </span>
                      <span className="k-menu-link-text">
                        {t('title_equipment')}
                      </span>
                    </span>
                  </Link>
                </li>
              )}

              <li
                className={`k-item k-menu-item ${
                  selectedIndex === 2 ? 'selected-menu-item' : ''
                }`}
              >
                <Link to="/resources" onClick={handleClick}>
                  <span className="k-link k-menu-link" role="presentation">
                    <span className="menu-icon menu-icon-resources">
                      {ResourcesSvg}
                    </span>
                    <span className="k-menu-link-text">
                      {t('title_resources')}
                    </span>
                  </span>
                </Link>
              </li>

              {isAuthenticated && (
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 3 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link
                    to="/dealers"
                    onClick={() => {
                      setDealerListData(data => ({
                        ...data,
                        dealerTab: DealerTabs.Locations,
                      }));
                      handleClick();
                    }}
                  >
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon menu-icon-dealers">
                        {DealersSvg}
                      </span>
                      <span className="k-menu-link-text">
                        {t('title_dealers')}
                      </span>
                    </span>
                  </Link>
                </li>
              )}

              {isAuthenticated && isAccessSharingPageEnabled && (
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 4 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link to="/access/equipment-and-fleets" onClick={handleClick}>
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon menu-icon-acess-sharing">
                        {AccessSharingSvg}
                      </span>
                      <span className="k-menu-link-text">
                        {t('title_access_sharing')}
                      </span>
                    </span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
          <div className="menu-left">
            {user && !user?.email_verified && <VerifyEmailMenu />}

            {(user?.email_verified || !user) && shouldShowSmallAdBanner() && (
              <SmallAdBanner
                className="kubota-menu__small-ad-banner"
                banner={adData?.smallBannerAd}
              />
            )}
          </div>

          {isAuthenticated && (
            <div className="menu-left">
              <ul className="k-reset k-header k-menu k-menu-vertical">
                <li
                  className={`k-item k-menu-item ${
                    selectedIndex === 5 ? 'selected-menu-item' : ''
                  }`}
                >
                  <Link to="/about" onClick={handleClick}>
                    <span className="k-link k-menu-link" role="presentation">
                      <span className="menu-icon menu-icon-about">
                        {AboutSvg}
                      </span>
                      <span className="k-menu-link-text">
                        {t('about_preference_title')}
                      </span>
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </ClickOutside>
    </>
  );
}
